// Conversation-level annotation that surfaces above the turn timeline
// when a meeting's metadata links it to a Calendar event (and vice
// versa). Bobbin's Calendar connector backfills meeting↔event links
// via `metadata.calendar_event_uri` on the meeting node and
// `metadata.meeting_uri` on the calendar_event node; this component
// renders that as a one-line strip.

import type { ConversationSummary } from "./types";

interface Props {
  conversation: ConversationSummary;
}

interface LinkMeta {
  uri?: string;
  title?: string;
  attendees?: number;
  conference_uri?: string;
}

function readLink(c: ConversationSummary): LinkMeta | null {
  const md = (c.metadata ?? {}) as Record<string, unknown>;
  if (c.kind === "meeting") {
    const uri = (md.calendar_event_uri as string | undefined) ?? undefined;
    const title = (md.calendar_event_title as string | undefined) ?? undefined;
    if (uri || title) {
      return { uri, title };
    }
    return null;
  }
  if (c.kind === "calendar_event") {
    const uri = (md.meeting_uri as string | undefined) ?? undefined;
    const title = (md.meeting_title as string | undefined) ?? undefined;
    const conference_uri = (md.conference_uri as string | undefined) ?? undefined;
    const attendees =
      typeof md.attendee_count === "number"
        ? (md.attendee_count as number)
        : undefined;
    if (uri || title || conference_uri || attendees != null) {
      return { uri, title, conference_uri, attendees };
    }
    return null;
  }
  return null;
}

export function CalendarAnnotation({ conversation }: Props): React.ReactElement | null {
  const link = readLink(conversation);
  if (link === null) return null;

  if (conversation.kind === "meeting") {
    return (
      <div className="cv-cal-annotation" data-direction="meeting-to-event">
        <span className="cv-cal-glyph" aria-hidden>📅</span>
        <span className="cv-cal-text">
          Linked Calendar event{link.title ? `: ${link.title}` : ""}
        </span>
      </div>
    );
  }

  return (
    <div className="cv-cal-annotation" data-direction="event-to-meeting">
      <span className="cv-cal-glyph" aria-hidden>📅</span>
      <span className="cv-cal-text">
        {link.attendees != null && `${link.attendees} attendees · `}
        {link.title ? `Meeting captured: ${link.title}` : "No meeting captured for this event"}
      </span>
      {link.conference_uri && (
        <a
          className="cv-cal-link"
          href={link.conference_uri}
          target="_blank"
          rel="noreferrer"
        >
          join
        </a>
      )}
    </div>
  );
}
